import { AlertTriangle, Eye, Lock } from 'lucide-react';
import { Select } from '@/shared/components';
import type { ArrivalStepPreviewResponse } from '@/shared/api';

interface Props {
  preview: ArrivalStepPreviewResponse | undefined;
  loading: boolean;
  /** Warum der Server nichts gerendert hat — dann prüft erst das Speichern die Felder. */
  error: string | null;
  selectedPropertyId: string | null;
  onSelectProperty: (propertyId: string) => void;
}

export function ArrivalStepPreview({
  preview,
  loading,
  error,
  selectedPropertyId,
  onSelectProperty,
}: Props) {
  return (
    <section className="border-line bg-paper/40 rounded-lg border p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <span className="text-ink-soft inline-flex items-center gap-1.5 text-xs font-medium">
          <Eye size={13} strokeWidth={2} aria-hidden />
          So sieht es der Gast
        </span>
        {preview && preview.apartments.length > 1 && (
          <Select
            aria-label="Vorschau für Apartment"
            className="max-w-56"
            value={selectedPropertyId ?? preview.propertyId}
            onChange={(event) => onSelectProperty(event.target.value)}
          >
            {preview.apartments.map((apartment) => (
              <option key={apartment.id} value={apartment.id}>
                {apartment.name}
              </option>
            ))}
          </Select>
        )}
      </div>

      {error ? (
        <p role="status" className="text-sealwax text-sm">
          Vorschau nicht verfügbar – {error} Unbekannte Felder prüft dann erst das Speichern.
        </p>
      ) : !preview ? (
        <p className="text-ink-soft text-sm">
          {loading ? 'Wird gerendert…' : 'Noch nichts zu zeigen.'}
        </p>
      ) : (
        <>
          <p className="text-ink text-sm font-semibold">{preview.title}</p>
          <p className="text-ink-soft mt-1 whitespace-pre-wrap text-sm leading-relaxed">
            {preview.body}
          </p>
          {preview.isTimeLocked && (
            <p className="text-ink-soft mt-2 inline-flex items-center gap-1.5 text-xs">
              <Lock size={11} strokeWidth={2} aria-hidden />
              Erst 12 Stunden vor Anreise vollständig sichtbar.
            </p>
          )}
          <ArrivalPreviewWarnings preview={preview} />
        </>
      )}
    </section>
  );
}

function ArrivalPreviewWarnings({ preview }: { preview: ArrivalStepPreviewResponse }) {
  if (preview.unknownPlaceholders.length === 0 && preview.gaps.length === 0) return null;
  return (
    <div className="border-line/60 mt-3 space-y-1.5 border-t pt-3">
      {preview.unknownPlaceholders.length > 0 && (
        <p className="text-sealwax flex items-start gap-1.5 text-xs">
          <AlertTriangle size={12} strokeWidth={2} className="mt-0.5 shrink-0" aria-hidden />
          <span>
            Unbekannte Platzhalter: {preview.unknownPlaceholders.join(', ')}. Das Speichern wird
            abgewiesen.
          </span>
        </p>
      )}
      {preview.gaps.map((gap) => (
        <p key={gap.token} className="text-ink-soft flex items-start gap-1.5 text-xs">
          <AlertTriangle
            size={12}
            strokeWidth={2}
            className="text-amber mt-0.5 shrink-0"
            aria-hidden
          />
          <span>
            <span className="font-mono">{`{{${gap.token}}}`}</span> bleibt leer bei:{' '}
            {gap.apartments.map((apartment) => apartment.name).join(', ')}
          </span>
        </p>
      ))}
    </div>
  );
}
